import type { BoardDoc } from '../../doc/BoardDoc';
import { rotatedBounds, unionRects, type Point } from '../../doc/geometry';
import { RICH_TYPES, type Item, type NewItem } from '../../doc/schema';
import { enqueueUpload } from '../../sync/uploadQueue';
import { asNewItems, relinkAssets, type Ctx } from '../actions';
import { getAssetMeta, storePreviewAsset } from '../assets/localAssets';
import { rasterize } from '../export/rasterize';
import { withFrameContents } from '../frames';
import { filterSelectable } from '../ink';
import { loadRich } from '../text/richLoader';
import { acquireDrawer, addCutout, cutoutItems, cutoutsOf } from './drawerDoc';

/** Longest side of a tile's preview render, in pixels (tiles are about 110 px, doubled for dense screens). */
const PREVIEW_PX = 256;

const TYPE_NAME: Record<Item['type'], string> = {
  frame: 'Frame',
  image: 'Image',
  note: 'Note',
  title: 'Title',
  richtext: 'Text',
  table: 'Table',
  todo: 'To-do list',
  kanban: 'Kanban',
  shape: 'Shape',
  stroke: 'Drawing',
  link: 'Link',
  file: 'File',
  boardcard: 'Board card',
  sheet: 'Sketch sheet',
};

/** What a new cutout is called until the user renames it: what it says, or the file an image came from. */
async function defaultName(items: Item[]): Promise<string> {
  const first = items[0];
  if (!first) return 'Cutout';
  if (items.length > 1) {
    const frame = items.find((i) => i.type === 'frame');
    if (frame?.type === 'frame' && frame.props.title.trim()) return frame.props.title.trim();
    return items.every((i) => i.type === first.type) ? `${items.length} × ${TYPE_NAME[first.type].toLowerCase()}` : `${items.length} items`;
  }
  let text = '';
  if (first.type === 'note' || first.type === 'title') text = first.props.text;
  else if (first.type === 'frame' || first.type === 'boardcard') text = first.props.title;
  else if (first.type === 'file') text = first.props.name;
  else if (first.type === 'link') text = first.props.title || first.props.url;
  else if (first.type === 'image') text = ((await getAssetMeta(first.props.assetId, false))?.name ?? '').replace(/\.[^./]*$/, '');
  text = text.trim().replace(/\s+/g, ' ');
  return text ? (text.length > 40 ? `${text.slice(0, 39)}…` : text) : TYPE_NAME[first.type];
}

export type DrawerAddResult = { ok: true; cutoutId: string; count: number } | { ok: false; reason: 'empty' | 'unavailable' | 'failed' };

/**
 * Saves items as one cutout in the drawer: a snapshot like the clipboard's, a frame bringing
 * its contents along, with every asset cloned into the drawer so the cutout outlives the board
 * it came from. The items themselves stay where they are.
 */
export async function addToDrawer({ board }: Ctx, ids: Iterable<string>, folderId: string | null): Promise<DrawerAddResult> {
  const wanted = new Set(ids);
  const picked = board.index.all().filter((i) => wanted.has(i.id));
  const expanded = new Set(withFrameContents(board, picked).map((i) => i.id));
  // bottom to top, so the saved pieces stack the way they did
  const live = board.index.all().filter((i) => expanded.has(i.id));
  const bounds = unionRects(live.map(rotatedBounds));
  if (!live.length || !bounds) return { ok: false, reason: 'empty' };
  const held = await acquireDrawer();
  if (!held) return { ok: false, reason: 'unavailable' };
  const { drawer, release } = held;
  try {
    if (live.some((i) => RICH_TYPES.has(i.type))) await loadRich();
    const snapshot = board.snapshotItems(live.map((i) => i.id));
    let thumbAssetId: string | null = null;
    try {
      const scale = Math.min(2, PREVIEW_PX / Math.max(bounds.w, bounds.h, 1));
      const canvas = await rasterize(board, live, bounds, { scale, background: null, padding: 0 });
      thumbAssetId = (await storePreviewAsset(canvas, drawer.boardId, 'cutout')).id;
      if (drawer.remote) void enqueueUpload(drawer.boardId, thumbAssetId);
    } catch (e) {
      console.warn('[drawer] no preview', e);
    }
    const name = await defaultName(picked);
    await relinkAssets(snapshot as NewItem[], board.boardId, drawer);
    const folder = folderId !== null && drawer.doc.getMap('drawerFolders').has(folderId) ? folderId : null;
    const cutoutId = addCutout(
      drawer,
      {
        name,
        folderId: folder,
        createdAt: Date.now(),
        w: Math.round(bounds.w),
        h: Math.round(bounds.h),
        count: snapshot.length,
        kind: snapshot.length === 1 ? snapshot[0]!.type : 'group',
        thumbAssetId,
      },
      snapshot,
    );
    return { ok: true, cutoutId, count: snapshot.length };
  } catch (e) {
    console.warn('[drawer] could not save the cutout', e);
    return { ok: false, reason: 'failed' };
  } finally {
    release();
  }
}

/**
 * Places a cutout on a board, centred on `at`: fresh items (groups of their own, the board's
 * automatic layers unless it still has the layer they were saved from) with their assets cloned
 * out of the drawer. The cutout stays in the drawer.
 */
export async function placeCutout({ board, pane }: Ctx, drawer: BoardDoc, cutoutId: string, at: Point): Promise<string[]> {
  if (!cutoutsOf(drawer).has(cutoutId)) return [];
  const items = cutoutItems(drawer, cutoutId);
  const bounds = unionRects(items.map(rotatedBounds));
  if (!items.length || !bounds) return [];
  if (items.some((i) => RICH_TYPES.has(i.type))) await loadRich();
  const dx = Math.round(at.x - bounds.x - bounds.w / 2);
  const dy = Math.round(at.y - bounds.y - bounds.h / 2);
  const inputs = asNewItems(items, dx, dy, (item) => (item.layerId && board.layer(item.layerId) ? item.layerId : undefined));
  await relinkAssets(inputs, drawer.boardId, board);
  const created = board.createItems(inputs);
  // what the current tool can select of it (ink belongs to the draw tool, the rest to the others)
  const s = pane.getState();
  if (s.tool !== 'select' && s.tool !== 'draw') s.setTool('select');
  s.select(filterSelectable(board, created, pane.getState().tool));
  return created;
}
